import { createSlice, current } from "@reduxjs/toolkit";
import SelectOptionForImport from "../../pages/datasets/importdataset/formsteps/selectoptionfordatasetstep";
import ImportFromDbStep from "../../pages/datasets/importdataset/formsteps/importfromdbstep";
import ImportFromFileStep from "../../pages/datasets/importdataset/formsteps/importfromfilestep";
import ChooseTableQuery from "../../pages/datasets/importdataset/formsteps/choosetablequerystep";
import ImportFromApiStep from "../../pages/datasets/importdataset/formsteps/importfromapistep";
import ReviewStep from "../../pages/datasets/importdataset/formsteps/reviewstep";
import Result from "../../pages/datasets/importdataset/formsteps/result";
import { api } from "../../api/httpclient";
import { addNotification } from "./notificationsslice";
import { notificationDurationMiddle } from "../../lib/utils";
import { SITE_API_URL } from "../../settings";

export const stepDbPages = [
  SelectOptionForImport,
  ImportFromDbStep,
  ChooseTableQuery,
  ReviewStep,
  Result,
];
export const stepFilePages = [
  SelectOptionForImport,
  ImportFromFileStep,
  ReviewStep,
  Result,
];
export const stepApiPages = [
  SelectOptionForImport,
  ImportFromApiStep,
  ReviewStep,
  Result,
];

export const databaseDDL = [
  { Id: 1, Title: "Azure SQL" },
  { Id: 2, Title: "SQL Server" },
  { Id: 3, Title: "MySQL" },
  { Id: 4, Title: "Oracle" },
  { Id: 5, Title: "PstogreSQL" },
];

export const ddlAPIMethods = [
  { Id: "GET", Title: "GET" },
  { Id: "POST", Title: "POST" },
];

export const maxSize = 2 * 1024 * 1024; //2Mb

const defaultFormInitialValues = {
  //common
  ImportFrom: "fromdb",
  SelectedDataset: "",

  //file
  File: null,
  Filename: "",
  FileId: "",
  SelectedSheet: "",
  StartingRow: 2,
  EndingRow: null,

  //database
  DatabaseTypeId: 2,
  DatabaseType: "SQL Server",
  Server: "",
  Database: "",
  Username: "",
  Password: "",

  //table
  ChooseFrom: "table",
  Table: "",
  Query: "",

  //api
  Url: "",
  Method: "Get",
  Headers: [{ key: "", value: "" }],
  ReccursEvery: 0,

  NumberOfRows: 0,
  RestOfConsumption: 0,
  DataSize: 0,
  SampleData: [],
  DataSampleOpen: true,
};

const initialState = {
  pageLoaded: false,
  pageDisposed: true,
  processing: false,
  processingText: "",
  mode: 1,

  currentStepIndex: 0,
  previousStepIndex: -1,
  previousStepValue: "Cancel",
  nextStepIndex: 1,
  nextStepValue: "Next",
  steps: [],
  stepPages: stepDbPages,
  stepperOrientation: "horizontal",
  lst_of_datasets: [],
  sheets: [],
  lst_of_tables: [],

  formKey: new Date().toString(),
  formInitialValues: { ...defaultFormInitialValues },
  formValues: { ...defaultFormInitialValues },
  formClientErrors: {},
  formServerErrors: [],

  toolsVisible: true,
  panes: [
    {
      size: "20%",
      min: "20%",
      max: "25%",
      collapsible: false,
    },
    {
      min: "70%",
      max: "80%",
      collapsible: false,
    },
  ],
};

const importDatasetPageSlice = createSlice({
  name: "importDatasetPage",
  initialState: initialState,
  reducers: {
    setInitialState: (state, action) => ({ ...initialState }),

    setProcessing: (state, action) => {
      const { processing = true, processingText = "" } = action.payload;
      return {
        ...state,
        processing: processing,
        processingText: processing == false ? "" : processingText,
      };
    },

    setPageValue: (state, action) => ({
      ...state,
      [action.payload.id]: action.payload.value,
    }),

    setPanes: (state, action) => ({ ...state, panes: { ...action.payload } }),
    setFormValue: (state, action) => {
      let id = action.payload.id;
      let value = action.payload.value;
      return {
        ...state,
        formValues: {
          ...state.formValues,
          [id]: value,
        },
      };
    },

    pageLoaded: (state, action) => {
      const pageState = current(state);
      if (pageState.pageDisposed) return state;
      return {
        ...state,
        pageLoaded: true,
        processing: false,
        processingText: "",
        pageDisposed: false,
        lst_of_datasets: action.payload,
      };
    },

    disposePage: (state, action) => {
      if (action.payload) return { ...initialState };
      else return { ...state, pageDisposed: false };
    },

    setCurrentStepIndex: (state, action) => {
      return {
        ...state,
        currentStepIndex: action.payload,
      };
    },

    setSteps: (state, action) => {
      return {
        ...state,
        steps: action.payload,
      };
    },

    setStepPages: (state, action) => {
      return {
        ...state,
        stepPages: action.payload,
      };
    },

    setNavigation: (state, action) => {
      const {
        previousStepIndex = -1,
        previousStepValue = "Cancel",
        nextStepIndex = 1,
        nextStepValue = "Next",
      } = action.payload;
      return {
        ...state,
        previousStepIndex: previousStepIndex,
        previousStepValue: previousStepValue,
        nextStepIndex: nextStepIndex,
        nextStepValue: nextStepValue,
      };
    },

    setSheets: (state, action) => {
      return {
        ...state,
        sheets: action.payload,
      };
    },

    setListOfTables: (state, action) => {
      return {
        ...state,
        lst_of_tables: action.payload,
      };
    },

    resetFileInfo(state, action) {
      return {
        ...state,
        formValues: {
          ...state.formValues,
          File: null,
          Filename: "",
          FileId: "",
          SelectedSheet: "",
          StartingRow: 2,
          EndingRow: null,
        },
      };
    },

    resetDatabaseInfo(state, action) {
      return {
        ...state,
        formValues: {
          ...state.formValues,
          DatabaseType: 2,
          Server: "",
          Database: "",
          Username: "",
          Password: "",
          Table: "",
          Query: "",
        },
      };
    },

    resetDatabaseTableInfo(state, action) {
      return {
        ...state,
        formValues: {
          ...state.formValues,
          Table: "",
          Query: "",
        },
      };
    },

    resetApiInfo(state, action) {
      return {
        ...state,
        formValues: {
          ...state.formValues,
          Url: "",
          Method: "",
          Headers: [{ key: "", value: "" }],
        },
      };
    },
  },
});

export default importDatasetPageSlice.reducer;

export const loadPage = (t) => async (dispatch, getState) => {
  //start with initial values
  dispatch(setInitialState());

  //undispose page
  dispatch(disposePage(false));

  //show loader
  dispatch(
    setProcessing({ processing: true, processingText: t("slices.loadingPage")})
  );

  try {
    var response = await api({
      url: "dataset/getuserdefinedwithsystemdatasetlist",
      method: "post",
      //headers:{'h':'1'}
    });

    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().importDatasetPage.pageDisposed) return;

    //it's ok to dispatch here
    let data = response.data.Data;
    let lst_of_datasets = [];
    data.Items.forEach((e) => {
      lst_of_datasets.push({ Id: e.Id, Title: e.Title });
    });

    console.log(lst_of_datasets);
    dispatch(pageLoaded(lst_of_datasets));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    return Promise.reject(err);
  }
};

export const getTablesFromDbConnection = (t) => async (dispatch, getState) => {
  const pageState = getState().importDatasetPage;

  //show loader
  dispatch(
    setProcessing({ processing: true, processingText: t("slices.updatingDataset")})
  );

  try {
    var response = await api({
      url: "/dataset/getTablesFromDbConnection",
      method: "post",
      data: {
        dataBaseTypeId: pageState.formValues.DatabaseType,
        server: pageState.formValues.Server,
        dataBase: pageState.formValues.Database,
        username: pageState.formValues.Username,
        password: pageState.formValues.Password,
      },
      //headers:{'h':'1'}
    });
    //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
    if (getState().importDatasetPage.pageDisposed) return;

    let data = response.data.Data;

    dispatch(setListOfTables(data.Tables));

    dispatch(setProcessing({ processing: false }));

    return Promise.resolve(response);
  } catch (err) {
    //i am here => could be any error included 200 and Succeeded=false
    if (err.data.Errors?.length > 0) {
      dispatch(
        addNotification({
          text: err.data.Errors[0].Message,
          type: "error",
          closable: true,
          duration: notificationDurationMiddle,
          position: "topRight",
        })
      );
      dispatch(setProcessing({ processing: false, processingText: "" }));
    }
    return Promise.reject(err);
  }
};

export const getExcelSheetsFromFile =
  (formData, currentUser, t) => async (dispatch, getState) => {
    //show loader
    dispatch(
      setProcessing({ processing: true, processingText: t("slices.loadingPage") })
    );
    try {
      // Prepare the Authorization header
      let headers = {};
      if (currentUser.IsAuthenticated && currentUser.Jwt) {
        headers["Authorization"] = `bearer ${currentUser.Jwt}`;
      }

      const res = await fetch(
        `${SITE_API_URL}/DataSet/getexcelsheetsfromfile`,
        {
          method: "POST",
          headers: headers, // Set the headers
          body: formData,
        }
      );

      const response = await res.json();

      //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
      if (getState().importDatasetPage.pageDisposed) return;

      if (response.Errors?.length > 0) {
        dispatch(
          addNotification({
            text: response.Errors[0].Message,
            type: "error",
            closable: true,
            duration: notificationDurationMiddle,
            position: "topRight",
          })
        );
        dispatch(setProcessing({ processing: false, processingText: "" }));
        return;
      }

      //it's ok to dispatch here
      let data = response.Data;

      //transform the response
      dispatch(setSheets(data.SheetNames));
      dispatch(setFormValue({ id: "FileId", value: data.FileId }));
      dispatch(setProcessing({ processing: false, processingText: "" }));

      return Promise.resolve(response);
    } catch (err) {
      //i am here => could be any error included 200 and Succeeded=false
      return Promise.reject(err);
    }
  };

export const getNumberOfRowsFromFile =
  (fileId, sheetname, importData, isDataReal, startingRow, endingRow, datasetId, t) =>
  async (dispatch, getState) => {
    //show loading
    dispatch(
      setProcessing({ processing: true, processingText: t("slices.loadingDataset")})
    );

    try {
      var response = await api({
        url: "dataset/getdatasetschemefromfile",
        method: "post",
        data: {
          FileId: fileId,
          SheetName: sheetname,
          ImportData: importData,
          IsDataReal: isDataReal,
          StartingRow: startingRow,
          EndingRow: endingRow,
          DatasetId: datasetId
        },
        //headers:{'h':'1'}
      });

      //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
      if (getState().importDatasetPage.pageDisposed) return;

      let data = response.data.Data;

      dispatch(setFormValue({ id: "NumberOfRows", value: data.NumberOfRows }));

      dispatch(setProcessing({ processing: false, processingText: "" }));

      return Promise.resolve(response);
    } catch (err) {
      //i am here => could be any error included 200 and Succeeded=false
      if (err.data.Errors?.length > 0) {
        dispatch(
          addNotification({
            text: err.data.Errors[0].Message,
            type: "error",
            closable: true,
            duration: notificationDurationMiddle,
            position: "topRight",
          })
        );
        dispatch(setProcessing({ processing: false, processingText: "" }));
      }
      return Promise.reject(err);
    }
  };

export const importDataFromFile =
  (datasetId, fileId, isDataReal, startingRow, endingRow, sheetname, t) =>
  async (dispatch, getState) => {
    //show loading
    dispatch(
      setProcessing({ processing: true, processingText: t("slices.loadingDataset")})
    );

    try {
      var response = await api({
        url: "dataset/importdatafromfile",
        method: "post",
        data: {
          DataSetId: datasetId,
          FileId: fileId,
          IsDataReal: isDataReal,
          StartingRow: startingRow,
          EndingRow: endingRow,
          SheetName: sheetname,
        },
        //headers:{'h':'1'}
      });

      //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
      if (getState().importDatasetPage.pageDisposed) return;

      let data = response.data.Data;

      if (data.Succeeded) {
        dispatch(setPageValue({ id: "resultApiUrl", value: data.ApiUrl }));
        dispatch(
          setPageValue({ id: "resultInsertedRows", value: data.InsertedRows })
        );
        dispatch(
          setPageValue({ id: "resultDatasetId", value: data.DataSetId })
        );
        dispatch(
          setPageValue({
            id: "resultMessage",
            value: t("slices.importedDataset"),
          })
        );
      }

      dispatch(setProcessing({ processing: false, processingText: "" }));

      return Promise.resolve(response);
    } catch (err) {
      //i am here => could be any error included 200 and Succeeded=false
      return Promise.reject(err);
    }
  };

export const getDatasetSchemeFromDBConnection =
  (table, query, dbConnection, t) => async (dispatch, getState) => {
    //show loading
    dispatch(
      setProcessing({ processing: true, processingText: t("slices.loadingDataset") })
    );

    try {
      var response = await api({
        url: "dataset/getdatasetschemefromdbconnection",
        method: "post",
        data: {
          TableName: table,
          Query: query,
          DataBaseConnection: dbConnection,
        },
        //headers:{'h':'1'}
      });

      //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
      if (getState().importDatasetPage.pageDisposed) return;

      let data = response.data.Data;

      if (data.Errors?.length > 0) {
        dispatch(
          addNotification({
            text: response.Errors[0].Message,
            type: "error",
            closable: true,
            duration: notificationDurationMiddle,
            position: "topRight",
          })
        );
        dispatch(setProcessing({ processing: false, processingText: "" }));
        return false;
      }

      dispatch(setFormValue({ id: "NumberOfRows", value: data.NumberOfRows }));
      dispatch(
        setFormValue({ id: "RestOfConsumption", value: data.RestOfConsumption })
      );
      dispatch(setFormValue({ id: "DataSize", value: data.DataSize }));
      dispatch(
        setFormValue({
          id: "SampleData",
          value: data.SampleData,
        })
      );

      dispatch(setProcessing({ processing: false, processingText: "" }));

      return Promise.resolve(response);
    } catch (err) {
      //i am here => could be any error included 200 and Succeeded=false
      if (err.data.Errors?.length > 0) {
        dispatch(
          addNotification({
            text: err.data.Errors[0].Message,
            type: "error",
            closable: true,
            duration: notificationDurationMiddle,
            position: "topRight",
          })
        );
        dispatch(setProcessing({ processing: false, processingText: "" }));
      }
      return Promise.reject(err);
    }
  };

export const importDataFromDB =
  (datasetId, cnxId, table, query, dbConnection, t) =>
  async (dispatch, getState) => {
    //show loading
    dispatch(
      setProcessing({ processing: true, processingText: t("slices.loadingDataset") })
    );

    try {
      var response = await api({
        url: "dataset/importdatafromdbconnection",
        method: "post",
        data: {
          DataSetId: datasetId,
          ConnectionId: cnxId,
          TableName: table,
          Query: query,
          DataBaseConnection: dbConnection,
        },
        //headers:{'h':'1'}
      });

      //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
      if (getState().importDatasetPage.pageDisposed) return;

      let data = response.data.Data;

      if (data.Succeeded) {
        dispatch(setPageValue({ id: "resultApiUrl", value: data.ApiUrl }));
        dispatch(
          setPageValue({ id: "resultInsertedRows", value: data.InsertedRows })
        );
        dispatch(
          setPageValue({ id: "resultDatasetId", value: data.DatasetId })
        );
        dispatch(
          setPageValue({
            id: "resultMessage",
            value: t("slices.importedDataset"),
          })
        );
      }
      dispatch(setProcessing({ processing: false, processingText: "" }));

      return Promise.resolve(response);
    } catch (err) {
      //i am here => could be any error included 200 and Succeeded=false
      return Promise.reject(err);
    }
  };

export const getSampleDataFromApiEndpoint =
  (datasetId, url, method, headers, t) => async (dispatch, getState) => {
    //show loading
    dispatch(
      setProcessing({ processing: true, processingText: t("slices.loadingDataset")})
    );

    try {
      var response = await api({
        url: "dataset/getsampledatafromapiendpoint",
        method: "post",
        data: {
          DataSetId: datasetId,
          Url: url,
          Method: method,
          Headers: headers,
        },
      });

      //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
      if (getState().importDatasetPage.pageDisposed) return;

      let data = response.data.Data;

      if (data.Errors?.length > 0) {
        dispatch(
          addNotification({
            text: response.Errors[0].Message,
            type: "error",
            closable: true,
            duration: notificationDurationMiddle,
            position: "topRight",
          })
        );
        dispatch(setProcessing({ processing: false, processingText: "" }));
        return;
      }

      dispatch(setFormValue({ id: "NumberOfRows", value: data.ImportedRows }));
      dispatch(
        setFormValue({ id: "RestOfConsumption", value: data.RestOfConsumption })
      );
      dispatch(setFormValue({ id: "DataSize", value: data.DataSize }));
      dispatch(
        setFormValue({
          id: "SampleData",
          value: data.SampleData,
        })
      );

      dispatch(setProcessing({ processing: false, processingText: "" }));

      return Promise.resolve(response);
    } catch (err) {
      //i am here => could be any error included 200 and Succeeded=false
      if (err.data.Errors?.length > 0) {
        dispatch(
          addNotification({
            text: err.data.Errors[0].Message,
            type: "error",
            closable: true,
            duration: notificationDurationMiddle,
            position: "topRight",
          })
        );
        dispatch(setProcessing({ processing: false, processingText: "" }));
        return;
      }

      return Promise.reject(err);
    }
  };

export const importDataByInvokingApiEndpoint =
  (datasetId, url, method, reccursEvery, headers, t) =>
  async (dispatch, getState) => {
    //show loading
    dispatch(
      setProcessing({ processing: true, processingText: t("slices.loadingDataset")})
    );

    try {
      var response = await api({
        url: "dataset/importdatabyinvokingapiendpoint",
        method: "post",
        data: {
          DataSetId: datasetId,
          Url: url,
          Method: method,
          RecurrsEvery: reccursEvery.Id,
          Headers: headers,
        },
      });

      //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
      if (getState().importDatasetPage.pageDisposed) return;

      let data = response.data.Data;

      if (data.Succeeded) {
        dispatch(
          setPageValue({ id: "resultInsertedRows", value: data.InsertedRows })
        );
        dispatch(
          setPageValue({ id: "resultDatasetId", value: data.DataSetId })
        );
        dispatch(
          setPageValue({
            id: "resultMessage",
            value: t("slices.importedDataset"),
          })
        );
      }
      dispatch(setProcessing({ processing: false, processingText: "" }));

      return Promise.resolve(response);
    } catch (err) {
      //i am here => could be any error included 200 and Succeeded=false
      return Promise.reject(err);
    }
  };

export const {
  setInitialState,
  setProcessing,
  setPageValue,
  setPanes,
  pageLoaded,
  setFormValue,
  createEntity,
  entityLoaded,
  entityInserted,
  entityUpdated,
  entityCloned,
  entityDeleted,
  disposePage,
  setCurrentStepIndex,
  setSteps,
  setStepPages,
  setSheets,
  setListOfTables,
  setNavigation,
  resetLookupTable,
  resetFileInfo,
  resetDatabaseInfo,
  resetDatabaseTableInfo,
  resetApiInfo,
} = importDatasetPageSlice.actions;
